import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Container,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import { useHtsActions, useHtsData } from 'src/context/providers/HtsContext';
import HtsDrawer, { HTS_CODE_LENGTH } from '../hts-drawer';

const IMPORT_ACCEPT = '.csv,.xlsx,.xls';

export default function HtsListView() {
  const settings = useSettingsContext();
  const { htsCodes, loading } = useHtsData();
  const { getHtsCodes, deleteHts, importHts } = useHtsActions();
  const fileInput = useRef(null);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [editing, setEditing] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    getHtsCodes();
  }, [getHtsCodes]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return htsCodes;
    return htsCodes.filter(
      (hts) => hts.code.toLowerCase().includes(term) || hts.name.toLowerCase().includes(term)
    );
  }, [htsCodes, search]);

  const openDrawer = (hts) => {
    setEditing(hts);
    setDrawerOpen(true);
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImporting(true);
    setImportResult(await importHts(file));
    setImporting(false);
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="HTS Codes"
        action={
          <Stack direction="row" spacing={1.5}>
            <input
              ref={fileInput}
              type="file"
              accept={IMPORT_ACCEPT}
              hidden
              onChange={handleImport}
            />
            <Button
              variant="outlined"
              startIcon={<Iconify icon="mdi:file-upload-outline" />}
              disabled={importing}
              onClick={() => fileInput.current?.click()}
            >
              {importing ? 'Importing…' : 'Import CSV / Excel'}
            </Button>
            <Button
              variant="contained"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={() => openDrawer(null)}
            >
              New HTS Code
            </Button>
          </Stack>
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      {importResult && (
        <Alert
          severity={importResult.skipped?.length ? 'warning' : 'success'}
          onClose={() => setImportResult(null)}
          sx={{ mb: 3 }}
        >
          Imported {importResult.rows} rows: {importResult.created} created, {importResult.updated}{' '}
          updated, {importResult.unchanged} unchanged
          {importResult.skipped?.length
            ? `, ${importResult.skipped.length} skipped (${importResult.skipped
                .slice(0, 10)
                .map((s) => `row ${s.row}: ${s.reason}`)
                .join('; ')}${importResult.skipped.length > 10 ? '; …' : ''})`
            : ''}
          .
        </Alert>
      )}

      <Card>
        <Stack sx={{ p: 2.5 }}>
          <TextField
            fullWidth
            placeholder="Search code or name…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            helperText="Import columns: code, name (or the first two columns without a header)"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        {loading && <LinearProgress color="success" />}

        <TableContainer>
          <Scrollbar>
            <Table sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 180 }}>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right" sx={{ width: 64 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((hts) => (
                  <TableRow key={hts.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDrawer(hts)}>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>{hts.code}</span>
                        {hts.code.length !== HTS_CODE_LENGTH && (
                          <Tooltip
                            title={`HTS codes have ${HTS_CODE_LENGTH} digits — this one has ${hts.code.length}`}
                          >
                            <Iconify icon="solar:danger-triangle-bold" sx={{ color: 'warning.main' }} />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{hts.name}</TableCell>
                    <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => setDeleting(hts)}>
                          <Iconify icon="solar:trash-bin-trash-bold" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && !visible.length && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      {search ? 'No HTS codes match your search' : 'No HTS codes yet — create one or import a file'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePagination
          component="div"
          count={visible.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(_, value) => setPage(value)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
        />
      </Card>

      <HtsDrawer open={drawerOpen} hts={editing} onClose={() => setDrawerOpen(false)} />

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title="Delete HTS code"
        content={
          <>
            Delete <strong>{deleting?.code}</strong> — {deleting?.name}?
          </>
        }
        action={
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              await deleteHts(deleting.id);
              setDeleting(null);
            }}
          >
            Delete
          </Button>
        }
      />
    </Container>
  );
}
